import { ApiRequest, Recaptcha } from "@plinknz/tah-website-elements";
import React, { useEffect, useState } from "react";
import * as SurveyType from "survey-react";
import { Loader } from "../../loader";
import { Survey } from "../../survey";
import { MARAE_BOOKING_ENDPOINTS } from "../../../config/forms/marae-booking";
import { BookingPayload } from "../../../config/forms/marae-booking/payload";
import { maraeApi } from "../../../service/forms/marae-booking/api";
import { createSurveyConfig } from "../../../service/forms/marae-booking/survey-config";
import { initializeAddressFinderWidget } from "../../survey-widgets/address-finder-widget";
import { initializeDatepickerWidget } from "../../survey-widgets/datepicker-widget";
import { applySurveyConfig } from "../../../utility/apply-survey-config";
import { transformMaraeBookingData } from "../../../utility/form-transforms/transform-marae-booking-data";

export interface MaraeBookingFormProps {
    request?: ApiRequest;
    manualRecaptchaLoaded?: (a: () => void) => void;
}

let payload: BookingPayload = null;

export const MaraeForm = ({
    request = maraeApi,
    manualRecaptchaLoaded,
}: MaraeBookingFormProps) => {
    const [surveyModel, updateSurvey] = useState<SurveyType.SurveyModel>(null);
    const [surveyLoading, updateSurveyLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>(null);
    const [recaptchaLoading, updateRecaptchaLoading] = useState<boolean>(true);

    const handleRecaptcha = async (token: string) => {
        payload = {
            ...payload,
            recaptcha: token,
        };

        // const payloadURL = bookingPayloadAsURL(payload);

        await request.post(MARAE_BOOKING_ENDPOINTS.marae, payload);
    };

    const sendDataToServer = async (survey: SurveyType.SurveyModel) => {
        updateSurveyLoading(true);

        try {
            grecaptcha.execute();
            payload = transformMaraeBookingData(survey);
        } catch (sendError) {
            console.error(sendError);

            if (sendError instanceof Error) {
                setError(sendError.message);
            }
        } finally {
            updateSurveyLoading(false);
        }
    };

    const recaptchaLoaded = () => updateRecaptchaLoading(false);

    if (manualRecaptchaLoaded) {
        manualRecaptchaLoaded(() => updateRecaptchaLoading(false));
    }

    useEffect(() => {
        (async () => {
            if (recaptchaLoading || surveyLoading || error || surveyModel) {
                return;
            }

            try {
                updateSurveyLoading(true);
                const survey = await import(
                    /* webpackChunkName: "survey-react" */ "survey-react"
                );
                await import(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    /* webpackChunkName: "surveyjs-widgets" */ "surveyjs-widgets" as any
                );

                // TODO: The run time imports above make it difficult to test
                // this without getting not wrapped in act errors
                // Need to figure out how to do this

                applySurveyConfig(survey);
                survey.surveyLocalization.locales.my.completingSurvey =
                    "Tino Pai - Thank you for your booking.";
                initializeDatepickerWidget(survey);
                initializeAddressFinderWidget(survey);

                const result = createSurveyConfig();
                const surveyInstance = new survey.Model(result);

                surveyInstance.locale = "my";

                updateSurvey(surveyInstance);
            } catch (fetchError) {
                console.warn("Error: ", error);
                setError(
                    error ||
                        "There was an error trying to retrieve information from the server. Please reload and try again."
                );
            } finally {
                updateSurveyLoading(false);
            }
        })();

        return () => {
            if (surveyModel) surveyModel.clear();
            updateSurvey(null);
        };
    }, [recaptchaLoading]);

    return (
        <div
            className="registration-form || constrain-width"
            data-testid="marae-booking-form">
            {((recaptchaLoading || surveyLoading) && (
                <div className="splash">
                    <Loader size="large" />
                </div>
            )) ||
                (error && (
                    <div data-testid="error">
                        <p>{error}</p>
                    </div>
                )) || (
                    <Survey model={surveyModel} onComplete={sendDataToServer} />
                )}
            <Recaptcha onReady={recaptchaLoaded} onSubmit={handleRecaptcha} />
        </div>
    );
};
